<script setup lang="ts">
import { ref } from 'vue';
import ButtonV2 from './ButtonV2.vue';
import Fade from '../Animation/Fade.vue';
import Slide from '../Animation/Slide.vue';

const { src, alt, imgClass, zoomable } = defineProps<{
    src: string
    alt?: string
    imgClass?: string,
    zoomable?: boolean
}>();

const isLoading = ref(true);
const isErrors = ref(false);
const modalShow = ref(false);

const handleImageLoad = () => {
    isLoading.value = false;
    isErrors.value = false;
};

const handleImageError = () => {
    isLoading.value = false;
    isErrors.value = true;
};

const reloadImage = () => {
    isLoading.value = true;
    isErrors.value = false;
};
</script>

<template>
    <div class="max-w-full flex flex-col items-center justify-center">
        <!-- Loading Placeholder -->
        <div role="status" class="w-full aspect-[16/9] animate-pulse flex items-center justify-center" v-if="isLoading">
            <div class="flex items-center justify-center w-full aspect-video bg-gray-300 rounded-lg">
                <svg class="w-10 h-10 text-gray-200" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor" viewBox="0 0 20 18">
                    <path
                        d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
                </svg>
            </div>
            <span class="sr-only">Loading...</span>
        </div>

        <!-- Reload Button -->
        <ButtonV2 color="info" type="icon" animation @click="reloadImage" v-if="isErrors">
            <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M16.1381 2.04034V5.79034C16.1381 5.9561 16.0722 6.11507 15.955 6.23228C15.8378 6.34949 15.6788 6.41533 15.5131 6.41534H11.7631C11.6395 6.41533 11.5186 6.37867 11.4159 6.30999C11.3131 6.24131 11.233 6.14371 11.1857 6.02951C11.1384 5.91531 11.126 5.78965 11.1501 5.66842C11.1742 5.54718 11.2337 5.43582 11.3211 5.3484L12.7542 3.91534L12.4194 3.5806C11.5454 2.70652 10.4317 2.11127 9.21933 1.87012C8.00695 1.62897 6.75028 1.75274 5.60825 2.22579C4.46621 2.69884 3.4901 3.49992 2.80334 4.52773C2.11658 5.55553 1.75003 6.76391 1.75003 8.00004C1.75003 9.23617 2.11658 10.4445 2.80334 11.4724C3.4901 12.5002 4.46621 13.3012 5.60825 13.7743C6.75028 14.2473 8.00695 14.3711 9.21933 14.13C10.4317 13.8888 11.5454 13.2936 12.4194 12.4195C12.4775 12.3614 12.5464 12.3153 12.6222 12.2838C12.6981 12.2523 12.7794 12.2361 12.8615 12.2361C12.9436 12.236 13.0249 12.2522 13.1008 12.2836C13.1767 12.315 13.2456 12.3611 13.3037 12.4191C13.3617 12.4772 13.4078 12.5461 13.4392 12.622C13.4706 12.6979 13.4868 12.7792 13.4867 12.8613C13.4867 12.9434 13.4705 13.0247 13.439 13.1006C13.4076 13.1764 13.3615 13.2453 13.3034 13.3033C12.2545 14.3522 10.9181 15.0666 9.46324 15.356C8.00837 15.6454 6.50036 15.4968 5.12991 14.9292C3.75945 14.3615 2.5881 13.4002 1.76399 12.1668C0.939871 10.9335 0.5 9.48341 0.5 8.00004C0.5 6.51667 0.939871 5.06661 1.76399 3.83324C2.5881 2.59986 3.75945 1.63856 5.12991 1.0709C6.50036 0.503246 8.00837 0.354723 9.46324 0.644118C10.9181 0.933513 12.2545 1.64783 13.3034 2.69673L13.6381 3.03143L15.0711 1.5984C15.1585 1.51099 15.2699 1.45146 15.3911 1.42734C15.5124 1.40322 15.638 1.41559 15.7522 1.4629C15.8665 1.5102 15.9641 1.59031 16.0327 1.69309C16.1014 1.79588 16.1381 1.91672 16.1381 2.04034Z"
                    fill="currentColor" />
            </svg>
        </ButtonV2>

        <!-- Image Container -->
        <img class='rounded-lg' :class="[
            { 'hidden': isLoading },
            { 'cursor-zoom-in': zoomable },
            imgClass]" :src="src" @load="handleImageLoad" @error="handleImageError" v-if="!isErrors"
            @click="modalShow = !modalShow" :alt="alt" />
        <Teleport to="body" v-if="zoomable">
            <Fade>
                <div class="fixed top-0 left-0 z-50 w-full h-full flex items-center justify-center overflow-hidden bg-black/50 backdrop-blur cursor-zoom-out"
                    @click="modalShow = false" v-if="modalShow">
                    <Slide direction="down" class="delay-300">
                        <img class='rounded-lg h-2/3 w-auto' :src="src" :alt="alt" />
                    </Slide>
                </div>
            </Fade>
        </Teleport>
    </div>
</template>